"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertFilterRelationsModelToJaql = exports.getFilterRelationsFromJaql = exports.mergeFilters = exports.getFilterCompareId = exports.isOperatorDescriptionNode = exports.isAttributeDescriptionNode = exports.isCloseBracketDescriptionNode = exports.isOpenBracketDescriptionNode = exports.UnknownRelationsNodeError = exports.findFilterByGuid = exports.filterRelationRulesToFilterRelationsModel = exports.isRelationsRule = exports.isRelationsRuleIdNode = exports.convertFilterRelationsModelToRelationRules = exports.getRelationsWithReplacedFilter = exports.calculateNewRelations = exports.isTrivialSingleNodeRelations = exports.combineFiltersAndRelations = exports.getFiltersArray = exports.splitFiltersAndRelations = exports.mergeFiltersOrFilterRelations = exports.isFilterRelations = void 0;
/* eslint-disable @typescript-eslint/default-param-last */
const index_js_1 = require("../../index.js");
const cloneDeep_js_1 = __importDefault(require("lodash-es/cloneDeep.js"));
const isArray_js_1 = __importDefault(require("lodash-es/isArray.js"));
const translatable_error_js_1 = require("../../translation/translatable-error.js");
/**
 * Type guard for checking if the provided filters are FilterRelations.
 *
 * @param filters - The filters to check.
 * @returns `true` if the filters are FilterRelations, `false` otherwise.
 * @group Filter Utilities
 */
function isFilterRelations(filters) {
    return (!!filters &&
        'operator' in filters &&
        (filters.operator === 'AND' || filters.operator === 'OR') &&
        !!filters.right &&
        !!filters.left);
}
exports.isFilterRelations = isFilterRelations;
/**
 * Merges source filters with target filters and recalculates relations.
 *
 * @param sourceFilters - The source filters or filter relations to merge.
 * @param targetFilters - The target filters or filter relations to merge with.
 * @returns Updated source filters merged with target filters. If the source filters are FilterRelations, the relations are recalculated.
 * @internal
 */
function mergeFiltersOrFilterRelations(sourceFilters = [], targetFilters = []) {
    if (!isFilterRelations(sourceFilters) && !isFilterRelations(targetFilters)) {
        return mergeFilters(sourceFilters, targetFilters);
    }
    const { filters: pureSourceFilters, relations: sourceRelations } = splitFiltersAndRelations(sourceFilters);
    const pureTargetFilters = getFiltersArray(targetFilters);
    const mergedFilters = mergeFilters(pureSourceFilters, pureTargetFilters);
    const mergedRelations = calculateNewRelations(pureSourceFilters, sourceRelations, mergedFilters);
    return combineFiltersAndRelations(mergedFilters, mergedRelations);
}
exports.mergeFiltersOrFilterRelations = mergeFiltersOrFilterRelations;
/**
 * Splits filters or filter relations into filters and relations rules.
 * @internal
 */
function splitFiltersAndRelations(filtersOrFilterRelations) {
    if (!filtersOrFilterRelations) {
        return { filters: [], relations: null };
    }
    if ((0, isArray_js_1.default)(filtersOrFilterRelations)) {
        return { filters: filtersOrFilterRelations, relations: null };
    }
    const filtersSet = new Set();
    function traverse(node) {
        if (isFilterNode(node)) {
            filtersSet.add(node);
            return { instanceid: node.config.guid };
        }
        if (isFilterRelations(node)) {
            const left = traverse(node.left);
            const right = traverse(node.right);
            return { left, right, operator: node.operator };
        }
        throw new UnknownRelationsNodeError();
    }
    const relations = traverse(filtersOrFilterRelations);
    return {
        filters: Array.from(filtersSet),
        relations,
    };
}
exports.splitFiltersAndRelations = splitFiltersAndRelations;
/**
 * Returns pure filters array from the given filters or filter relations.
 * @internal
 */
function getFiltersArray(filtersOrFilterRelations) {
    if (!filtersOrFilterRelations) {
        return [];
    }
    return splitFiltersAndRelations(filtersOrFilterRelations).filters;
}
exports.getFiltersArray = getFiltersArray;
/**
 * Combines filters and relations into a single FilterRelations object.
 * If the relations are empty or relations are trivial (single node), the filters are returned as is.
 * @internal
 */
function combineFiltersAndRelations(filters, relations) {
    if (!relations || isTrivialSingleNodeRelations(relations)) {
        return filters;
    }
    const resultRelations = traverse(relations);
    return isFilterNode(resultRelations) ? [resultRelations] : resultRelations;
    function traverse(node) {
        if (isRelationsRuleIdNode(node)) {
            return filters.find((filter) => filter.config.guid === node.instanceid);
        }
        if (isRelationsRule(node)) {
            return {
                left: traverse(node.left),
                right: traverse(node.right),
                operator: node.operator,
            };
        }
        throw new UnknownRelationsNodeError();
    }
}
exports.combineFiltersAndRelations = combineFiltersAndRelations;
/** Type guard for checking if a node is a single relations node (trivial case when relations are needless).
 * @internal
 */
function isTrivialSingleNodeRelations(relations) {
    return !!relations && 'instanceid' in relations;
}
exports.isTrivialSingleNodeRelations = isTrivialSingleNodeRelations;
/**
 * Calculates new relations based on the changes in filters.
 * @internal
 */
function calculateNewRelations(prevFilters, prevRelations, newFilters) {
    // If there are no previous relations - no need to recalculate them
    if (prevRelations === null) {
        return null;
    }
    const performedActions = diffFilters(prevFilters, newFilters);
    if (performedActions.length === 0) {
        return prevRelations;
    }
    return performedActions.reduce((relations, action) => {
        switch (action.type) {
            case 'add':
                return addFilterToRelations(action.payload, relations);
            case 'remove':
                return removeFilterFromRelations(action.payload, relations);
        }
    }, prevRelations);
}
exports.calculateNewRelations = calculateNewRelations;
/**
 * Compares two filters and determines if they are equal in terms of relations.
 */
function areFiltersEqualForRelations(filter1, filter2) {
    return filter1.config.guid === filter2.config.guid;
}
/**
 * Replaces a filter in the relations tree with a new filter.
 * @internal
 */
function getRelationsWithReplacedFilter(relations, filterToReplace, newFilter) {
    if (!relations) {
        return null;
    }
    const replacedRelations = traverse(relations);
    return replacedRelations;
    function traverse(node) {
        if (isRelationsRuleIdNode(node)) {
            return node.instanceid === filterToReplace.config.guid
                ? { instanceid: newFilter.config.guid }
                : node;
        }
        if (isRelationsRule(node)) {
            return {
                left: traverse(node.left),
                right: traverse(node.right),
                operator: node.operator,
            };
        }
        throw new UnknownRelationsNodeError();
    }
}
exports.getRelationsWithReplacedFilter = getRelationsWithReplacedFilter;
/**
 * Compares two arrays of Filter objects and determines the actions needed
 * to transform prevFilters into newFilters.
 *
 * @param prevFilters - The original array of filters.
 * @param newFilters - The updated array of filters.
 * @param isEqualFilters - A function to determine if two filters are equal.
 * @returns An array of FilterAction objects representing the changes.
 */
function diffFilters(prevFilters, newFilters) {
    const actions = [];
    // Clone the arrays to avoid mutating the original data
    const prevFiltersCopy = [...prevFilters];
    const newFiltersCopy = [...newFilters];
    // Determine removals
    prevFiltersCopy.forEach((prevFilter) => {
        const existsInNew = newFiltersCopy.some((newFilter) => areFiltersEqualForRelations(prevFilter, newFilter));
        if (!existsInNew) {
            actions.push({ type: 'remove', payload: prevFilter });
        }
    });
    // Determine additions
    newFiltersCopy.forEach((newFilter) => {
        const existsInPrev = prevFiltersCopy.some((prevFilter) => areFiltersEqualForRelations(newFilter, prevFilter));
        if (!existsInPrev) {
            actions.push({ type: 'add', payload: newFilter });
        }
    });
    return actions;
}
/**
 * Adds a filter to root of relations tree as a new node connected with AND operator.
 * If the relations tree is empty, the filter is added as the root node.
 */
function addFilterToRelations(filter, relations) {
    if (!relations) {
        return { instanceid: filter.config.guid };
    }
    return {
        left: relations,
        right: { instanceid: filter.config.guid },
        operator: 'AND',
    };
}
/**
 * Removes a filter from the relations tree.
 * If the filter is not found, the relations tree is returned as is.
 */
function removeFilterFromRelations(filter, relations) {
    if (!relations) {
        return null;
    }
    if (isTrivialSingleNodeRelations(relations)) {
        return relations.instanceid === filter.config.guid ? null : relations;
    }
    const leftAfterRemoval = removeFilterFromRelations(filter, relations.left);
    const rightAfterRemoval = removeFilterFromRelations(filter, relations.right);
    if (leftAfterRemoval === null && !!rightAfterRemoval) {
        return rightAfterRemoval;
    }
    if (rightAfterRemoval === null && !!leftAfterRemoval) {
        return leftAfterRemoval;
    }
    if (leftAfterRemoval === null && rightAfterRemoval === null) {
        return null;
    }
    return {
        left: leftAfterRemoval,
        right: rightAfterRemoval,
        operator: relations.operator,
    };
}
/**
 * Converts filter relations model from Fusion to internal CSDK filter relations rules.
 * @internal
 */
function convertFilterRelationsModelToRelationRules(filterRelationsModel, filters) {
    if (!filterRelationsModel) {
        return null;
    }
    function traverse(node) {
        if (isModelIdNode(node)) {
            return { instanceid: node.instanceId };
        }
        if (isModelBracketNode(node)) {
            return traverse(node.value);
        }
        if (isModelRelationNode(node)) {
            const newNode = {
                operator: node.operator,
                left: traverse(node.left),
                right: traverse(node.right),
            };
            return newNode;
        }
        if (isModelCascadeNode(node)) {
            // CSDK filter relations model work with cascading filters as with a single node.
            const cascadingFilterForThisCascade = filters.find((filter) => {
                if (!(0, index_js_1.isCascadingFilter)(filter)) {
                    return false;
                }
                const levelIds = filter.filters.map((f) => f.config.guid);
                return node.levels.every((levelFilter) => levelIds.includes(levelFilter.instanceId));
            });
            if (cascadingFilterForThisCascade) {
                return { instanceid: cascadingFilterForThisCascade.config.guid };
            }
            throw new Error('Broken filter relations model. Cascading filter not found.');
        }
        throw new Error('Broken filter relations model. Unknown node type.');
    }
    return traverse(filterRelationsModel);
}
exports.convertFilterRelationsModelToRelationRules = convertFilterRelationsModelToRelationRules;
function isModelIdNode(node) {
    return 'instanceId' in node;
}
function isModelBracketNode(node) {
    return 'value' in node;
}
function isModelRelationNode(node) {
    return 'operator' in node;
}
function isModelCascadeNode(node) {
    return 'levels' in node && (0, isArray_js_1.default)(node.levels);
}
/**
 * @internal
 */
function isRelationsRuleIdNode(node) {
    return 'instanceid' in node;
}
exports.isRelationsRuleIdNode = isRelationsRuleIdNode;
/**
 * @internal
 */
function isRelationsRule(node) {
    return 'operator' in node;
}
exports.isRelationsRule = isRelationsRule;
function isFilterNode(node) {
    return 'config' in node && 'guid' in node.config;
}
/**
 * Converts internal CSDK filter relations rules to filter relations model for Fusion.
 * @internal
 */
function filterRelationRulesToFilterRelationsModel(filterRelationRules, filters) {
    if (!filterRelationRules) {
        return undefined;
    }
    function traverse(node) {
        if (isRelationsRuleIdNode(node)) {
            // Check if this instanceid corresponds to a cascading filter
            const filter = filters.find((f) => f.config.guid === node.instanceid);
            if (filter && (0, index_js_1.isCascadingFilter)(filter)) {
                // Build a CascadingIdentifier node
                const levels = filter.filters.map((levelFilter) => ({
                    type: 'Identifier',
                    instanceId: levelFilter.config.guid,
                }));
                return {
                    type: 'CascadingIdentifier',
                    levels,
                };
            }
            else {
                // Build an Identifier node
                return {
                    type: 'Identifier',
                    instanceId: node.instanceid,
                };
            }
        }
        else if (isRelationsRule(node)) {
            // Build a LogicalExpression node
            return {
                type: 'LogicalExpression',
                operator: node.operator,
                left: traverse(node.left),
                right: traverse(node.right),
            };
        }
        else {
            throw new Error('Unknown node type in filter relations rules.');
        }
    }
    return traverse(filterRelationRules);
}
exports.filterRelationRulesToFilterRelationsModel = filterRelationRulesToFilterRelationsModel;
/**
 * @internal
 */
function findFilterByGuid(filters, guid) {
    return filters.find((filter) => filter.config.guid === guid);
}
exports.findFilterByGuid = findFilterByGuid;
/**
 * Error thrown when an unknown node type is encountered in filter relations.
 * @internal
 */
class UnknownRelationsNodeError extends Error {
    constructor() {
        super('Broken filter relations. Unknown node type.');
    }
}
exports.UnknownRelationsNodeError = UnknownRelationsNodeError;
/**
 * @internal
 */
function isOpenBracketDescriptionNode(node) {
    return node.nodeType === 'openBracket';
}
exports.isOpenBracketDescriptionNode = isOpenBracketDescriptionNode;
/**
 * @internal
 */
function isCloseBracketDescriptionNode(node) {
    return node.nodeType === 'closeBracket';
}
exports.isCloseBracketDescriptionNode = isCloseBracketDescriptionNode;
/**
 * @internal
 */
function isAttributeDescriptionNode(node) {
    return node.nodeType === 'attribute';
}
exports.isAttributeDescriptionNode = isAttributeDescriptionNode;
/**
 * @internal
 */
function isOperatorDescriptionNode(node) {
    return node.nodeType === 'operator';
}
exports.isOperatorDescriptionNode = isOperatorDescriptionNode;
/**
 * Gets a unique identifier for a filter, combining its attribute expression and granularity if available.
 *
 * @param {Filter} filter - The filter object to generate the unique identifier for.
 * @returns {string} - The unique identifier for the filter.
 * @internal
 */
function getFilterCompareId(filter) {
    if ((0, index_js_1.isCascadingFilter)(filter)) {
        return filter.filters.map(getFilterCompareId).join('-');
    }
    // TODO: remove fallback on 'filter.jaql()' after removing temporal 'jaql()' workaround from filter translation layer
    const { attribute: filterAttribute } = filter;
    const filterJaql = filter.jaql().jaql;
    const expression = filterAttribute.expression || filterJaql.dim;
    const granularity = filterAttribute.granularity ||
        ((filterJaql === null || filterJaql === void 0 ? void 0 : filterJaql.datatype) === 'datetime'
            ? index_js_1.DimensionalLevelAttribute.translateJaqlToGranularity(filterJaql)
            : '');
    return `${expression}${granularity}`;
}
exports.getFilterCompareId = getFilterCompareId;
/**
 * Merges two arrays of filter objects, prioritizing 'targetFilters' over 'sourceFilters',
 * and removes duplicates based on filter compare id.
 *
 * Saves the 'sourceFilters' filters order, while adds new filters to the end of the array.
 *
 * @param {Filter[]} [sourceFilters=[]] - The source array of filter objects.
 * @param {Filter[]} [targetFilters=[]] - The target array of filter objects.
 * @returns {Filter[]} - The merged array of filter objects.
 * @internal
 */
function mergeFilters(sourceFilters = [], targetFilters = []) {
    const filters = [...sourceFilters];
    targetFilters.forEach((filter) => {
        // filters will always have findIndex as sourceFilters fall back to an empty array
        const existingFilterIndex = filters.findIndex((existingFilter) => getFilterCompareId(filter) === getFilterCompareId(existingFilter));
        const isFilterAlreadyExist = existingFilterIndex !== -1;
        if (isFilterAlreadyExist) {
            filters[existingFilterIndex] = filter;
        }
        else {
            filters.push(filter);
        }
    });
    return filters;
}
exports.mergeFilters = mergeFilters;
/**
 * Replaces nodes of filter reations tree with fetched filters by instance id.
 *
 * @param filters - The filters.
 * @param highlights - The highlights
 * @param filterRelations - Fetched filter relations.
 * @returns Filter relations with filters in nodes.
 * @internal
 */
/* eslint-disable-next-line sonarjs/cognitive-complexity */
function getFilterRelationsFromJaql(filters, highlights, filterRelations) {
    if (!filterRelations) {
        return filters;
    }
    // If highlights are present, nodes in filter relations reference both filters and highlights
    // and thus cannot be replaced with filters. Return filters in this case.
    if (highlights.length) {
        return filters;
    }
    const mergedFilterRelations = (0, cloneDeep_js_1.default)(filterRelations);
    function traverse(node) {
        if ('instanceid' in node) {
            const filter = filters.find((filter) => filter.config.guid === node.instanceid);
            if (!filter) {
                throw new translatable_error_js_1.TranslatableError('errors.unknownFilterInFilterRelations');
            }
            return filter;
        }
        if ('operator' in node) {
            const newNode = { operator: node.operator };
            if ('left' in node) {
                newNode.left = traverse(node.left);
            }
            if ('right' in node) {
                newNode.right = traverse(node.right);
            }
            return newNode;
        }
        return node;
    }
    return traverse(mergedFilterRelations);
}
exports.getFilterRelationsFromJaql = getFilterRelationsFromJaql;
/**
 * Converts filter relations model to filter relations jaql.
 *
 * @param filterRelations - Filter relations model.
 * @returns Filter relations jaql.
 * @internal
 */
function convertFilterRelationsModelToJaql(filterRelations) {
    if (!filterRelations) {
        return filterRelations;
    }
    const convertedFilterRelations = (0, cloneDeep_js_1.default)(filterRelations);
    function traverse(node) {
        if ('instanceId' in node) {
            return { instanceid: node.instanceId };
        }
        if ('value' in node) {
            return traverse(node.value);
        }
        if ('operator' in node) {
            const newNode = { operator: node.operator };
            if ('left' in node) {
                newNode.left = traverse(node.left);
            }
            if ('right' in node) {
                newNode.right = traverse(node.right);
            }
            return newNode;
        }
        return node;
    }
    return traverse(convertedFilterRelations);
}
exports.convertFilterRelationsModelToJaql = convertFilterRelationsModelToJaql;
